import { useFrame, useThree } from '@react-three/fiber';
import { useMemo, useRef } from 'react';
import * as THREE from 'three';
import type { GeoNode } from '../../location';
import { DOT_PX, FLAT_Z, RADIUS } from '../constants';
import { flat, sphere } from '../geo';
import { DOT_FRAG, DOT_VERT } from '../shaders';
import { toSRGB } from '../toSRGB';
import type { MorphRef } from '../types';

export function Dot({
  node,
  morphRef,
  revealRef,
}: {
  node: GeoNode;
  morphRef: MorphRef;
  revealRef: MorphRef;
}) {
  const materialRef = useRef<THREE.ShaderMaterial>(null);
  const dpr = useThree((s) => s.gl.getPixelRatio());
  const geometry = useMemo(() => {
    const p = sphere(node.lat, node.lon, RADIUS.dot);
    const f = flat(node.lat, node.lon, FLAT_Z.dot);
    const g = new THREE.BufferGeometry();
    g.setAttribute(
      'position',
      new THREE.Float32BufferAttribute([p.x, p.y, p.z], 3)
    );
    g.setAttribute(
      'aFlat',
      new THREE.Float32BufferAttribute([f.x, f.y, f.z], 3)
    );
    return g;
  }, [node]);
  const uniforms = useMemo(
    () => ({
      uMorph: { value: 0 },
      uSize: { value: DOT_PX * dpr },
      uReveal: { value: 0 },
      uColor: { value: toSRGB(node.color) },
    }),
    [dpr, node.color]
  );
  useFrame(() => {
    const material = materialRef.current;
    if (!material) return;
    material.uniforms.uMorph.value = morphRef.current;
    material.uniforms.uReveal.value = revealRef.current;
  });
  return (
    <points geometry={geometry}>
      <shaderMaterial
        ref={materialRef}
        uniforms={uniforms}
        vertexShader={DOT_VERT}
        fragmentShader={DOT_FRAG}
        transparent={true}
        depthWrite={false}
      />
    </points>
  );
}
